// ATLAS-5793: pure helpers that keep a content <style> block through the premium exportInlineStyles export and apply it,
// scoped to one editor's editable, while editing.
//
// exportInlineStyles drops every <style> element from its output. `restoreStyleBlocks` puts the blocks of the editor's own
// data (the source of truth) back at their original position. For display, `buildScopedContentCss` parses the block with
// the CSSOM only (never forwarding authored text), confines every selector to the editable and re-verifies the result.
//
// SYNC: This file exists in two byte-identical copies:
//   Frontend/Atlas.Spa.Backoffice/src/app/shared/widgets/ckeditor-content-style.ts
//   Frontend/Atlas.Spa.Portal/src/app/shared/widgets/ckeditor-content-style.ts
// Keep them in sync; any change goes into both (operator decision 2026-10-13: no shared front-end module).
//
// No runtime imports: type-only imports from the CKEditor typings.
import type { Model } from 'https://cdn.ckeditor.com/typings/ckeditor5.d.ts';

export const CONTENT_STYLE_SCOPE_ATTRIBUTE = 'data-wl-content-style-scope'; // on the editable root
export const CONTENT_STYLE_OWNER_ATTRIBUTE = 'data-wl-content-style-for'; // on the owned <style>

// ASCII whitespace only. Not \s: that matches U+00A0, and &nbsp; must count as content.
const ASCII_WHITESPACE_ONLY = /^[ \t\n\f\r]*$/;
const ASCII_WHITESPACE = /[ \t\n\f\r]/;

function parseInert(html: string): HTMLTemplateElement {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template;
}

/** CSS text of every <style> element in `html`, in document order (parsed in an inert <template>). */
export function collectStyleBlocks(html: string): string[] {
  return Array.from(parseInert(html).content.querySelectorAll('style')).map((style) => style.textContent ?? '');
}

/** `htmlContent` of every GHS `htmlStyle` model element under the model's roots, in document order. */
export function collectModelStyleBlocks(model: Model): string[] {
  const blocks: string[] = [];
  for (const rootName of model.document.getRootNames()) {
    const root = model.document.getRoot(rootName);
    if (!root) {
      continue;
    }
    for (const { item } of model.createRangeIn(root)) {
      if ((item as any).is?.('element', 'htmlStyle')) {
        const content = (item as any).getAttribute('htmlContent');
        if (typeof content === 'string') {
          blocks.push(content);
        }
      }
    }
  }
  return blocks;
}

/** Top-level CSSStyleRule entries of `css`, parsed with `new CSSStyleSheet().replaceSync`. [] on any throw. */
export function filterStyleRules(css: string): CSSStyleRule[] {
  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
    return Array.from(sheet.cssRules).filter((rule): rule is CSSStyleRule => rule instanceof CSSStyleRule);
  } catch {
    return [];
  }
}

/** Splits at top-level occurrences of a separator, respecting (), [], quoted strings and backslash escapes. */
function splitTopLevel(text: string, isSeparator: (ch: string) => boolean): string[] {
  const parts: string[] = [];
  let current = '';
  let depth = 0;
  let quote = '';
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '\\') {
      current += ch + (text[i + 1] ?? '');
      i++;
      continue;
    }
    if (quote) {
      if (ch === quote) quote = '';
      current += ch;
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (ch === '(' || ch === '[') {
      depth++;
    } else if ((ch === ')' || ch === ']') && depth > 0) {
      depth--;
    } else if (depth === 0 && isSeparator(ch)) {
      parts.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  parts.push(current);
  return parts;
}

interface SelectorToken {
  combinator: boolean;
  text: string; // for a combinator: ' ', '>', '+' or '~'
}

/** Compounds and combinators of one selector member. */
function tokenizeMember(member: string): SelectorToken[] {
  const tokens: SelectorToken[] = [];
  let compound = '';
  let pendingCombinator = '';
  let depth = 0;
  let quote = '';
  const flush = () => {
    if (compound) {
      tokens.push({ combinator: false, text: compound });
      compound = '';
    }
  };
  const emitCombinator = () => {
    if (pendingCombinator) {
      tokens.push({ combinator: true, text: pendingCombinator });
      pendingCombinator = '';
    }
  };
  for (let i = 0; i < member.length; i++) {
    const ch = member[i];
    if (ch === '\\') {
      emitCombinator();
      compound += ch + (member[i + 1] ?? '');
      i++;
      continue;
    }
    if (quote) {
      if (ch === quote) quote = '';
      compound += ch;
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      emitCombinator();
      compound += ch;
    } else if (ch === '(' || ch === '[') {
      depth++;
      emitCombinator();
      compound += ch;
    } else if ((ch === ')' || ch === ']') && depth > 0) {
      depth--;
      compound += ch;
    } else if (depth > 0) {
      compound += ch;
    } else if (ASCII_WHITESPACE.test(ch)) {
      flush();
      if (!pendingCombinator) pendingCombinator = ' ';
    } else if (ch === '>' || ch === '+' || ch === '~') {
      flush();
      pendingCombinator = ch;
    } else {
      emitCombinator();
      compound += ch;
    }
  }
  flush();
  return tokens;
}

const ROOT_COMPOUND = /^(html|body|:root)$/i;

function joinTokens(tokens: SelectorToken[]): string {
  return tokens.map((t) => (t.combinator ? (t.text === ' ' ? ' ' : ` ${t.text} `) : t.text)).join('');
}

function scopeSelectorAttribute(scopeToken: string): string {
  return `[${CONTENT_STYLE_SCOPE_ATTRIBUTE}="${scopeToken.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`;
}

function scopeMember(member: string, scope: string): string | null {
  const trimmed = member.trim();
  if (!trimmed || /:host/i.test(trimmed)) {
    return null;
  }
  const tokens = tokenizeMember(trimmed);
  if (!tokens.length) {
    return null;
  }
  if (tokens[0].combinator) {
    // a leading '>' is a child of the root; '+' and '~' would select a sibling of the root
    if (tokens[0].text !== '>' || tokens.length < 2) {
      return null;
    }
    return `${scope}${joinTokens(tokens)}`;
  }
  let index = 0;
  let lastCombinator = ' ';
  let consumed = false;
  while (index < tokens.length && !tokens[index].combinator && ROOT_COMPOUND.test(tokens[index].text)) {
    consumed = true;
    if (index + 1 >= tokens.length) {
      return scope; // the whole member maps onto the editable root itself
    }
    const combinator = tokens[index + 1].text;
    if (combinator === '+' || combinator === '~') {
      return null;
    }
    lastCombinator = combinator;
    index += 2;
  }
  if (!consumed) {
    return `${scope} ${joinTokens(tokens)}`;
  }
  const rest = joinTokens(tokens.slice(index));
  return rest ? `${scope}${lastCombinator === '>' ? ' > ' : ' '}${rest}` : scope;
}

/** Scopes a CSSOM-normalised selector list to `[data-wl-content-style-scope="<token>"]`; '' if no member survives. */
export function scopeSelectorList(selectorText: string, scopeToken: string): string {
  const scope = scopeSelectorAttribute(scopeToken);
  return splitTopLevel(selectorText, (ch) => ch === ',')
    .map((member) => scopeMember(member, scope))
    .filter((member): member is string => !!member)
    .join(', ');
}

function isConfined(selectorText: string, scope: string): boolean {
  const members = splitTopLevel(selectorText, (ch) => ch === ',').map((m) => m.trim());
  return (
    members.length > 0 &&
    members.every((member) => {
      if (member === scope) return true;
      if (!member.startsWith(scope + ' ')) return false;
      const rest = member.slice(scope.length + 1).trimStart();
      return !!rest && !rest.startsWith('+') && !rest.startsWith('~');
    })
  );
}

/** filter, scope, re-parse, verify, CSSOM-serialise. '' when nothing survives. */
export function buildScopedContentCss(cssBlocks: readonly string[], scopeToken: string): string {
  const scope = scopeSelectorAttribute(scopeToken);
  const scoped: string[] = [];
  for (const block of cssBlocks) {
    for (const rule of filterStyleRules(block)) {
      const selector = scopeSelectorList(rule.selectorText, scopeToken);
      const declarations = rule.style.cssText;
      if (selector && declarations) {
        scoped.push(`${selector} { ${declarations} }`);
      }
    }
  }
  if (!scoped.length) {
    return '';
  }
  return filterStyleRules(scoped.join('\n'))
    .filter((rule) => isConfined(rule.selectorText, scope))
    .map((rule) => rule.cssText)
    .join('\n');
}

/** Replaces every case-insensitive `</style` with `<\/style`; CSS meaning is unchanged. */
export function neutraliseStyleEndTag(css: string): string {
  return css.replace(/<\/style/gi, '<\\/style');
}

function significantChildren(parent: Node): Node[] {
  return Array.from(parent.childNodes).filter(
    (node) =>
      (node.nodeType === Node.ELEMENT_NODE && node.nodeName !== 'STYLE') ||
      (node.nodeType === Node.TEXT_NODE && !ASCII_WHITESPACE_ONLY.test((node as Text).data))
  );
}

interface StyleAnchor {
  style: HTMLStyleElement;
  path: number[];
  before: number;
}

function anchorOf(style: HTMLStyleElement, root: Node): StyleAnchor {
  const path: number[] = [];
  const parent = style.parentNode as Node;
  for (let node: Node = parent; node !== root; node = node.parentNode as Node) {
    path.unshift(significantChildren(node.parentNode as Node).indexOf(node));
  }
  const siblings = Array.from(parent.childNodes);
  const significant = significantChildren(parent);
  let before = -1;
  for (let i = siblings.indexOf(style) + 1; i < siblings.length; i++) {
    const at = significant.indexOf(siblings[i]);
    if (at >= 0) {
      before = at;
      break;
    }
  }
  return { style, path, before };
}

/** Returns `exportedHtml` with every <style> block of `sourceHtml` restored at its original position. */
export function restoreStyleBlocks(sourceHtml: string, exportedHtml: string): string {
  if (!/<style/i.test(sourceHtml ?? '') || !exportedHtml) {
    return exportedHtml;
  }
  const source = parseInert(sourceHtml).content;
  const template = parseInert(exportedHtml);
  const target = template.content;

  // blocks the export already holds are not restored again (idempotent; two identical blocks are each restored once)
  const present = new Map<string, number>();
  target.querySelectorAll('style').forEach((style) => {
    const text = style.textContent ?? '';
    present.set(text, (present.get(text) ?? 0) + 1);
  });

  const missing: StyleAnchor[] = [];
  source.querySelectorAll('style').forEach((style) => {
    const text = style.textContent ?? '';
    const remaining = present.get(text) ?? 0;
    if (remaining > 0) {
      present.set(text, remaining - 1);
      return;
    }
    missing.push(anchorOf(style, source));
  });
  if (!missing.length) {
    return exportedHtml;
  }

  for (const { style, path, before } of missing) {
    // stop at the deepest node that resolved: a block is never dropped
    let parent: Node = target;
    for (const step of path) {
      const next = significantChildren(parent)[step];
      if (!next || next.nodeType !== Node.ELEMENT_NODE) {
        break;
      }
      parent = next;
    }
    // create in the parent's namespace so a <style> under <svg> serialises escaped, not raw
    const block = template.ownerDocument.createElementNS(
      (parent as Element).namespaceURI ?? 'http://www.w3.org/1999/xhtml',
      'style',
    );
    Array.from(style.attributes).forEach((attr) => block.setAttribute(attr.name, attr.value));
    block.textContent = neutraliseStyleEndTag(style.textContent ?? '');
    const reference = before >= 0 ? significantChildren(parent)[before] : undefined;
    parent.insertBefore(block, reference ?? null);
  }
  return template.innerHTML;
}

// R3: CKEditor's HtmlDataProcessor wraps input in <body> so a leading <style> stays in the content, but skips the wrap when
// <html>, <head>, <body> or <meta> appears in the first 10,000 characters (ckeditor5-engine 48.1.1, HtmlDataProcessor._toDom).
// The parser then hoists a <style> into <head>, which CKEditor never reads. Legacy templates carry <meta http-equiv>, so their
// block was lost before restoreStyleBlocks could act. Only the <style> blocks are carried over: other head content (<title>
// text, <link>, <meta>) stays dropped, as today.
const CKEDITOR_SKIPS_BODY_WRAP = /<(?:html|body|head|meta)(?:\s[^>]*)?>/i;

/**
 * Returns `html` unchanged unless CKEditor's own parse would put <style> blocks in <head>; then returns a <body> holding
 * those blocks (in document order) followed by the body content. Returns `html` unchanged if anything throws.
 */
export function prepareHtmlForParse(html: string): string {
  try {
    if (!CKEDITOR_SKIPS_BODY_WRAP.test(html.trim().slice(0, 10_000))) {
      return html; // CKEditor wraps it in <body> itself
    }
    const doc = new DOMParser().parseFromString(html, 'text/html'); // inert: no scripts, no resource loads
    const headStyles = Array.from(doc.head.querySelectorAll('style'));
    if (!headStyles.length) {
      return html;
    }
    return `<body>${headStyles.map((el) => el.outerHTML).join('')}${doc.body.innerHTML}</body>`;
  } catch {
    return html;
  }
}
